import { defineStore } from "pinia";
import { ref } from "vue";
import leaveTypeService from "@/services/hrm/leaveTypeService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useLeaveTypeStore = defineStore("leaveType", () => {
  const items = ref([]);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    search: "",
    enabled: "",      // "", "1", "0"
    per_page: 25,
    page: 1,
  });

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await leaveTypeService.getAll(params);
      const data = res.data;

      if (data?.data && (data?.meta || data?.total !== undefined)) {
        items.value = data.data;
        pagination.value = {
          total: data.meta?.total ?? data.total ?? data.data.length,
          per_page: data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
          current_page: data.meta?.current_page ?? data.current_page ?? 1,
        };
      } else {
        items.value = Array.isArray(data) ? data : [];
        pagination.value = {
          total: items.value.length,
          per_page: params.per_page ?? 25,
          current_page: 1,
        };
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch leave types";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await leaveTypeService.create(payload);
      toast("Leave type created");
      await fetchAll({ page: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await leaveTypeService.update(id, payload);
      toast("Leave type updated");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await leaveTypeService.delete(id);
      toast("Leave type deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      const res = await leaveTypeService.bulkDelete(ids);
      const kept = Number(res?.data?.kept || 0);
      toast(
        kept > 0 ? res.data.message : "Selected leave types deleted",
        kept > 0 ? "warning" : "success"
      );
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to bulk delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function toggleEnabled(id, is_enabled) {
    try {
      await leaveTypeService.toggle(id, is_enabled);
      await fetchAll();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to toggle", "error");
      throw e;
    }
  }

  return {
    items, pagination, loading, error, filters,
    fetchAll, create, update, remove, bulkDelete, toggleEnabled
  };
});
